// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import {
  CircleX,
  User,
  Brain,
  Globe,
  Star,
  Building2,
  CheckCircle,
  XCircle,
  Trash2,
  Loader2,
} from "lucide-react";
import { usePermissions } from "../../hooks/usePermissions";

const BookingDetails = ({
  booking,
  onUpdateStatus,
  onClose,
  detailsLoading,
}) => {
  if (!booking) return null;

  const { userPermissions } = usePermissions();
  const [status, setStatus] = useState(booking.status || "pending");
  const [actionLoading, setActionLoading] = useState(false);
  const [localBooking, setLocalBooking] = useState(booking);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  useEffect(() => {
    setStatus(booking.status || "pending");
    setLocalBooking(booking);
  }, [booking.status, booking]);

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
      case "completed":
        return "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300";
      case "pending":
        return "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300";
      case "cancelled":
      case "rejected":
        return "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300";
      case "expired":
        return "bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200";
      default:
        return "bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200";
    }
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
  };

  const handleStatusUpdate = async (newStatus) => {
    setActionLoading(true);
    try {
      await onUpdateStatus(booking.uuid, newStatus);
      setLocalBooking((prev) => ({ ...prev, status: newStatus }));
      setStatus(newStatus);
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusSave = async () => {
    if (status !== localBooking.status) {
      setActionLoading(true);
      try {
        await onUpdateStatus(booking.uuid, status);
        setLocalBooking((prev) => ({ ...prev, status: status }));
      } catch (error) {
        console.error("Error updating status:", error);
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const statusOptions = [
    { value: "pending", label: "Ausstehend" },
    { value: "confirmed", label: "Bestätigt" },
    { value: "completed", label: "Abgeschlossen" },
    { value: "cancelled", label: "Storniert" },
    { value: "rejected", label: "Abgelehnt" },
    { value: "expired", label: "Abgelaufen" },
  ];

  const getLlmLabelInfo = (llmLabel) => {
    switch (llmLabel) {
      case "U":
        return {
          label: "Unbedenklich",
          className:
            "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700",
        };
      case "E":
        return {
          label: "Explizit problematisch",
          className:
            "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-700",
        };
      case "V":
        return {
          label: "Subtile/kodierte Hinweise",
          className:
            "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-700",
        };
      default:
        return {
          label: "N/A",
          className:
            "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-slate-600 text-gray-800 dark:text-slate-200 border border-gray-200 dark:border-slate-500",
        };
    }
  };

  // Only show actual LLM results when a run has completed (llm_general set to true/false or 0/1)
  const hasValidLlmResults =
    booking.llm_general === true ||
    booking.llm_general === false ||
    booking.llm_general === 0 ||
    booking.llm_general === 1;
  const llmLabelInfo = getLlmLabelInfo(booking.llm_label);

  const formatDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d)) return null;
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
  };

  const getBookingDays = () => {
    if (booking.booking_days) {
      return booking.booking_days;
    }
    if (Array.isArray(booking.items)) {
      const itemWithBookingDays = booking.items.find(
        (item) => item.booking_days,
      );
      return itemWithBookingDays ? itemWithBookingDays.booking_days : null;
    }

    return null;
  };

  const formatZeitraum = (timeSlot) => {
    if (!timeSlot) return "N/A";
    const start = formatDate(timeSlot.startTime);
    const end = formatDate(timeSlot.endTime);
    const apiBookingDays = getBookingDays();
    const duration =
      apiBookingDays ||
      (typeof timeSlot.duration === "number"
        ? timeSlot.duration
        : timeSlot.startTime && timeSlot.endTime
          ? Math.max(
              1,
              Math.round(
                (new Date(timeSlot.endTime) - new Date(timeSlot.startTime)) /
                  (1000 * 60 * 60 * 24),
              ),
            )
          : null);

    const daysLabel =
      duration != null
        ? `(${duration} ${duration === 1 ? " Tag" : "Tage"})`
        : "";
    if (start && end) return `${start} - ${end}${daysLabel}`;
    return start || end || "N/A";
  };

  const formatDateTime = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d)) return null;
    const dd = d.getDate();
    const mm = d.getMonth() + 1;
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
  };
  const formatDateRange = (timeSlot) => {
    if (!timeSlot) return "N/A";
    const formattedDate = formatDateTime(timeSlot);
    return formattedDate || "N/A";
  };

  // Map booked_scenario_id to Paket label (1, 2, 3) or null to hide
  const getMappedPaketLabel = (scenarioId) => {
    const idNum = Number(scenarioId);
    if ([3, 6, 9].includes(idNum)) return 3;
    if ([1, 4, 7].includes(idNum)) return 1;
    if ([2, 5, 8].includes(idNum)) return 2;
    return null;
  };
  const paketLabel = getMappedPaketLabel(booking.booked_scenario_id);
  const hasParentKit =
    Array.isArray(booking?.items) &&
    booking.items.some(
      (it) => it.vhp_product_type === "kit" && it.layout_position === "none",
    );
  const derivePricingModel = (items) => {
    if (!Array.isArray(items))
      return { groups: [], standalone: [], computedTotal: booking.total || 0 };

    const parents = items.filter(
      (it) => it.vhp_product_type === "kit" && it.layout_position === "none",
    );

    // Map parent paket tags to parent item
    const paketKeyToParent = new Map();
    parents.forEach((p) => {
      (p.paket || []).forEach((tag) => paketKeyToParent.set(String(tag), p));
    });

    const childOf = (item) => {
      const tags = item?.paket || [];
      for (const t of tags) {
        if (paketKeyToParent.has(String(t)))
          return paketKeyToParent.get(String(t));
      }
      return null;
    };

    const groups = parents.map((parent) => ({ parent, children: [] }));

    const standalone = [];
    items.forEach((it) => {
      if (parents.includes(it)) return;
      const p = childOf(it);
      if (p) {
        const group = groups.find((g) => g.parent === p);
        if (group) group.children.push(it);
      } else {
        standalone.push(it);
      }
    });

    const toNumber = (val) => {
      if (typeof val === "number") return val;
      if (typeof val === "string") return Number(val.replace(",", "."));
      return 0;
    };
    const computedTotal =
      groups.reduce((sum, g) => sum + toNumber(g.parent.total_price), 0) +
      standalone.reduce((sum, it) => sum + toNumber(it.total_price), 0);

    return { groups, standalone, computedTotal: computedTotal.toFixed(2) };
  };

  const pricingModel = derivePricingModel(booking.items || []);
  return (
    <div className="md:w-1/2 w-full transition-all sticky top-3 duration-300 booking-details">
      <div
        className="bg-white dark:bg-slate-800 shadow dark:border dark:border-slate-700 rounded-lg md:overflow-y-auto overflow-y-hidden relative"
        style={{ scrollbarWidth: "thin" }}
      >
        {detailsLoading && (
          <div className="absolute h-full inset-0 z-10 flex items-center justify-center">
            <div className="flex items-center space-x-3 bg-white dark:bg-slate-700 rounded-lg p-4 shadow">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 dark:border-blue-400" />
              <span className="text-sm text-slate-700 dark:text-slate-200">
                Lade Details...
              </span>
            </div>
          </div>
        )}
        <div
          className="details"
          style={{
            height: isMobile ? "auto" : "calc(100dvh - 1.5rem)",
            opacity: detailsLoading ? 0.15 : 1,
          }}
        >
          <div className="px-4 py-5 sm:p-6">
            <div className="mb-6 w-full relative">
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                  Buchungsdetails
                </h3>
                <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                  <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 font-mono">
                    {booking.bookingNumber}
                  </p>
                  <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 font-mono">
                    {formatDateRange(booking.booking_time)}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-500 dark:text-slate-400 dark:hover:text-slate-300 absolute top-0 right-0"
                aria-label="Close"
              >
                <CircleX className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                  Grundinformationen
                </h4>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">
                      Veranstaltungstitel
                    </dt>
                    <dd className="text-sm text-slate-900 dark:text-slate-200 mt-1">
                      {booking.title || "N/A"}
                    </dd>
                  </div>
                  {(userPermissions?.includes("booking.approve") ||
                    userPermissions?.includes("booking.approve")) &&
                    (userPermissions?.includes("booking.reject") ||
                      userPermissions?.includes("booking.reject")) &&
                    (userPermissions?.includes("booking.cancel") ||
                      userPermissions?.includes("booking.cancel")) && (
                      <div>
                        <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">
                          Status
                        </dt>
                        <dd className="flex items-center gap-2">
                          <select
                            value={status}
                            onChange={handleStatusChange}
                            disabled={actionLoading}
                            className={`text-sm rounded-full px-3 py-1 font-medium border ${getStatusColor(status)} disabled:opacity-50 dark:border-transparent`}
                          >
                            {statusOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {status !== localBooking.status && (
                            <button
                              onClick={handleStatusSave}
                              disabled={actionLoading}
                              className="px-3 py-1 text-xs bg-blue-600 dark:bg-blue-500 text-white rounded-full hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {actionLoading ? "..." : "Speichern"}
                            </button>
                          )}
                        </dd>
                      </div>
                    )}
                  <div>
                    <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">
                      Veranstaltungstyp
                    </dt>
                    <dd className="text-sm text-slate-900 dark:text-slate-200 mt-1">
                      {booking.eventType || "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">
                      Paket
                    </dt>
                    <dd className="text-sm text-slate-900 dark:text-slate-200 mt-1">
                      {booking.package || "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">
                      E-Mail
                    </dt>
                    <dd className="text-sm text-slate-900 dark:text-slate-200 mt-1">
                      {booking.email || "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">
                      Gesamtbetrag
                    </dt>
                    <dd className="text-sm font-bold text-slate-900 dark:text-slate-100 mt-1">
                      €{booking.total}
                    </dd>
                  </div>
                </dl>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  Kontakt & Organisation
                </h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="border dark:border-slate-600 rounded-lg p-3 dark:bg-slate-700/30">
                    <h5 className="text-xs font-medium text-gray-700 dark:text-slate-300 mb-2">
                      Ansprechpartner
                    </h5>
                    {booking.user ? (
                      <>
                        <p className="text-sm text-gray-900 dark:text-slate-200">
                          {booking.user.first_name +
                            " " +
                            booking.user.last_name || "N/A"}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-slate-400">
                          {booking.user.email || booking.email || "N/A"}
                        </p>
                        {booking.user.status && (
                          <p className="text-sm text-gray-500 dark:text-slate-400">
                            {booking.user.status}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        N/A
                      </p>
                    )}
                  </div>
                  <div className="border dark:border-slate-600 rounded-lg p-3 dark:bg-slate-700/30">
                    <h5 className="text-xs font-medium text-gray-700 dark:text-slate-300 mb-2">
                      Organisation
                    </h5>
                    {booking.organization ? (
                      <>
                        <p className="text-sm text-gray-900 dark:text-slate-200">
                          {booking.organization.name || "N/A"}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-slate-400">
                          {booking.organization.website || "N/A"}
                        </p>
                        <span className="inline-flex items-left py-0.5 rounded text-xs font-medium text-slate-600 dark:text-slate-300">
                          {booking.organization.llm_www_summary || "N/A"}
                        </span>
                      </>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        N/A
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                  Veranstaltungsdetails
                </h4>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-slate-400">
                      Zeitraum
                    </dt>
                    <dd className="text-sm text-gray-900 dark:text-slate-200">
                      {formatZeitraum(booking.timeSlot)}
                    </dd>
                    <dd className="text-sm text-gray-900 dark:text-slate-200 hidden">
                      {booking.timeSlot?.startTime} -{" "}
                      {booking.timeSlot?.endTime}
                      <span className="text-gray-500 dark:text-slate-400 ml-2 hidden">
                        ({booking.timeSlot?.duration} days)
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-slate-400">
                      Teilnehmende
                    </dt>
                    <dd className="text-sm text-slate-900 dark:text-slate-200 mt-1">
                      {booking.participants || "N/A"} Personen
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-slate-400">
                      Veranstaltungsgröße
                    </dt>
                    <dd className="text-sm text-gray-900 dark:text-slate-200">
                      {booking.eventSize || "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-slate-400">
                      Buchungstage
                    </dt>
                    <dd className="text-sm text-gray-900 dark:text-slate-200">
                      {getBookingDays() || "N/A"}{" "}
                      {getBookingDays() === 1 ? "Tag" : "Tage"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-slate-400">
                      Beschreibung
                    </dt>
                    <dd className="text-sm text-gray-900 dark:text-slate-200 mt-1 break-words">
                      {booking.eventDescription || "N/A"}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="bg-gray-50 dark:bg-slate-700/40 rounded-lg p-4 dark:border dark:border-slate-600">
                <div className="mb-4">
                  <div className="flex flex-col sm:flex-row justify-between sm:items-center items-start mb-1">
                    <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      {paketLabel !== null
                        ? `${getBookingDays() || "N/A"} ${getBookingDays() === 1 ? "Tag" : "Tage"} ${booking.scenario_description}`
                        : "Buchungdetails"}
                    </h4>
                    <div className="text-xs text-slate-600 dark:text-slate-400">
                      Preis inkl. 19% MwSt.
                    </div>
                  </div>

                  {pricingModel.standalone.map((item, index) => (
                    <div
                      className="flex flex-col sm:flex-row justify-between sm:items-center items-start sm:pl-3 pl-0 py-1"
                      key={`standalone-${index}`}
                    >
                      <div className="text-sm text-slate-800 dark:text-slate-200">
                        {item.counter} x {item.vhp_name}
                        {item.unit && (
                          <>
                            , <span className="font-medium">{item.unit}</span>
                          </>
                        )}
                      </div>
                      <div className="whitespace-nowrap text-sm text-slate-900 dark:text-slate-100 text-right">
                        €{Number(item.total_price).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                {paketLabel !== null && hasParentKit && (
                  <div className="mb-4">
                    {pricingModel.groups.map((group, gi) => (
                      <React.Fragment key={`group-${gi}`}>
                        <div className="flex justify-between items-center">
                          <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1 flex items-center">
                            {paketLabel !== null
                              ? `Paket ${paketLabel}`
                              : "Paket"}
                          </h4>
                          <div className="text-sm text-slate-900 dark:text-slate-100">
                            €{Number(group.parent.total_price).toFixed(2)}
                          </div>
                        </div>
                        {group.children.map((child, ci) => (
                          <div
                            className="flex justify-between items-center sm:pl-3 pl-0 py-1"
                            key={`child-${gi}-${ci}`}
                          >
                            <div className="text-sm text-slate-700 dark:text-slate-300">
                              {child.counter} x {child.vhp_name} {child.unit}
                            </div>
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                  </div>
                )}

                <div className="border-t border-slate-200 dark:border-slate-600 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-900 dark:text-slate-100">
                      Gesamtpreis inkl. 19% MwSt.
                    </span>
                    <span className="text-sm font-bold text-gray-900 dark:text-slate-100">
                      €{booking.total}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm text-gray-900 dark:text-slate-200">
                      MwSt. 19%:
                    </span>
                    <span className="text-sm text-gray-900 dark:text-slate-200">
                      €{((Number(booking.total) || 0) * 0.19).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center">
                  <Brain className="w-4 h-4 mr-2" />
                  KI-Bewertung
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-slate-400">
                      Allgemeine Bewertung
                    </span>
                    {hasValidLlmResults ? (
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          booking.llm_general === true ||
                          booking.llm_general === 1
                            ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                            : "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
                        }`}
                      >
                        {booking.llm_general === true ||
                        booking.llm_general === 1
                          ? "Pass"
                          : "Fail"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-500">
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        processing...
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-slate-400">
                      Risiko-Label
                    </span>
                    {hasValidLlmResults ? (
                      <span className={llmLabelInfo.className}>
                        <CircleX className="w-3 h-3 mr-1" />
                        {llmLabelInfo.label}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-500">
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        processing...
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-500 dark:text-slate-400">
                        Regelwerk 1
                      </span>
                      <div className="mt-1 flex items-center">
                        <div className="flex-1 bg-gray-200 dark:bg-slate-600 rounded-full h-2">
                          <div
                            className="bg-indigo-600 dark:bg-indigo-500 h-2 rounded-full"
                            style={{
                              width: `${Math.min(((booking.llm_ruleset1 || 0) / 20) * 100, 100)}%`,
                            }}
                          ></div>
                        </div>
                        <span className="ml-2 text-sm text-gray-700 dark:text-slate-300">
                          {booking.llm_ruleset1 || "0"}/20
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500 dark:text-slate-400">
                        Regelwerk 2
                      </span>
                      <div className="mt-1 flex items-center">
                        <div className="flex-1 bg-gray-200 dark:bg-slate-600 rounded-full h-2">
                          <div
                            className="bg-indigo-600 dark:bg-indigo-500 h-2 rounded-full"
                            style={{
                              width: `${Math.min(((booking.llm_ruleset2 || 0) / 20) * 100, 100)}%`,
                            }}
                          ></div>
                        </div>
                        <span className="ml-2 text-sm text-gray-700 dark:text-slate-300">
                          {booking.llm_ruleset2 || "0"}/20
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="border dark:border-slate-600 rounded p-2 dark:bg-slate-700/30">
                      <Globe className="w-4 h-4 mx-auto mb-1 text-gray-400 dark:text-slate-500" />
                      <p className="text-xs text-gray-500 dark:text-slate-400 break-words">
                        Web-Plausibilität
                      </p>
                      <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                        {booking.llm_www_plausi || "N/A"}
                      </p>
                    </div>
                    <div className="border dark:border-slate-600 rounded p-2 dark:bg-slate-700/30">
                      <Star className="w-4 h-4 mx-auto mb-1 text-gray-400 dark:text-slate-500" />
                      <p className="text-xs text-gray-500 dark:text-slate-400 break-words">
                        Reputations-Fit
                      </p>
                      <p className="text-sm font-semibold text-yellow-600 dark:text-yellow-400">
                        {booking.organization?.llm_reputation_fit || "N/A"}
                      </p>
                    </div>
                    <div className="border dark:border-slate-600 rounded p-2 dark:bg-slate-700/30">
                      <Building2 className="w-4 h-4 mx-auto mb-1 text-gray-400 dark:text-slate-500" />
                      <p className="text-xs text-gray-500 dark:text-slate-400 break-words">
                        Gemeinnützig-Fit
                      </p>
                      <p className="text-sm font-semibold text-yellow-600 dark:text-yellow-400">
                        {booking.organization?.llm_nonprofit_fit || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div>
                    <dt className="text-sm font-semibold text-gray-500 dark:text-slate-400">
                      KI-Zusammenfassung
                    </dt>
                    <dd className="text-sm text-gray-900 dark:text-slate-200 mt-1 p-3 bg-gray-50 dark:bg-slate-700/50 rounded dark:border dark:border-slate-600">
                      {booking.eventEvaluation ||
                        booking.organization?.llm_www_summary ||
                        "N/A"}
                    </dd>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <span className="text-sm text-gray-500 dark:text-slate-400">
                      Prioritätsfaktor
                    </span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                      {booking.llm_priority_factor || "N/A"}
                    </span>
                  </div>

                  <div>
                    <dt className="pt-6 border-t dark:border-slate-600 text-sm font-semibold text-gray-500 dark:text-slate-400">
                      Begründung
                    </dt>
                    <dd
                      className="text-sm text-gray-900 dark:text-slate-200 mt-1 p-3 bg-gray-50 dark:bg-slate-700/50 rounded dark:border dark:border-slate-600"
                      dangerouslySetInnerHTML={{
                        __html: booking.llm_reasoning || "N/A",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons with permission checks */}
            <div className="pt-6 border-t border-slate-200 dark:border-slate-600 mt-6 flex items-center flex-wrap gap-3">
              {(userPermissions?.includes("booking.approve") ||
                userPermissions?.includes("booking.approve")) && (
                <button
                  onClick={() => handleStatusUpdate("confirmed")}
                  disabled={actionLoading}
                  className="flex-1 inline-flex justify-center items-center px-6 py-3 text-sm font-semibold rounded-xl text-white bg-primary-color dark:bg-primary-color/50 hover:bg-primary-color-dark dark:hover:bg-primary-color/60 transition-colors duration-200 shadow-lg shadow-primary-color/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Speichern
                </button>
              )}
              {(userPermissions?.includes("booking.reject") ||
                userPermissions?.includes("booking.reject")) && (
                <button
                  onClick={() => handleStatusUpdate("pending")}
                  disabled={actionLoading}
                  className="flex-1 inline-flex justify-center items-center px-6 py-3 border border-slate-300 dark:border-slate-500 text-sm font-semibold rounded-xl text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600 dark:border-slate-400 mr-2"></div>
                  ) : (
                    <XCircle className="h-4 w-4 mr-2" />
                  )}
                  Nachfragen
                </button>
              )}
              {(userPermissions?.includes("booking.reject") ||
                userPermissions?.includes("booking.reject")) && (
                <button
                  onClick={() => handleStatusUpdate("rejected")}
                  disabled={actionLoading}
                  className="flex-1 inline-flex justify-center items-center px-6 py-3 border border-rose-300 dark:border-rose-600 text-sm font-semibold rounded-xl text-rose-700 dark:text-rose-300 bg-white dark:bg-slate-700 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-rose-600 dark:border-rose-400 mr-2"></div>
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Ablehnen
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetails;
